import { useCallback, useState } from "react"
import { Typography, LinearProgress, Box } from "@mui/material"
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline"
import { Accept, useDropzone } from "react-dropzone"
import fileDownload from "js-file-download"

import Modal from "shared/Modal"
import { toast } from "react-toastify"
import { batchService } from "services/batchService"
import { Dropzone, DragActive, DragAccept, DragReject, BtnUpload, BtnDownload } from "./styles"

interface IModalCreateMany {
  isOpen: boolean
  setIsOpen: (value: boolean) => void
  refresh: () => void
}

export const DialogUpload = ({ isOpen, setIsOpen, refresh }: IModalCreateMany) => {
  const [file, setFile] = useState<FormData | null>(null)
  const [isUploading, setIsUploading] = useState<boolean | null>(null)
  const [fileName, setFileName] = useState("")
  const [isLoadingUpload, setIsLoadingUpload] = useState(false)
  const [isLoadingDownload, setIsLoadingDownload] = useState(false)

  const onDropAccepted = useCallback((acceptedFiles: File[]) => {
    setIsUploading(true)
    const uploadedFile = acceptedFiles[0]
    if (!uploadedFile) return

    const formData = new FormData()
    formData.append("file", uploadedFile)

    setFile(formData)
    setFileName(uploadedFile.name)
    setIsUploading(false)
  }, [])

  const handleCloseDialog = () => {
    setIsOpen(false)
    setIsLoadingUpload(false)
    setIsLoadingDownload(false)
    setIsUploading(null)
    setFile(null)
    setFileName("")
  }

  const handleCreateBatches = async () => {
    try {
      setIsLoadingUpload(true)
      if (!file) throw new Error("Nenhum arquivo foi selecionado.")
      await batchService.createByExcel(file)
      toast.success("Upload realizado com sucesso!")
      refresh()
      handleCloseDialog()
    } catch (error: any) {
      const errorMessage = error?.message || error?.msg
      toast.error(
        errorMessage ??
          "Eita, deu ruim pra fazer o upload do excel. Verifique se todos dados estão corretos.",
      )
    } finally {
      setIsLoadingUpload(false)
    }
  }

  const handleExcelDownload = async () => {
    try {
      setIsLoadingDownload(true)
      const data = await batchService.downloadExcel()
      fileDownload(data, "modelo_lotes_em_massa.xls")
      toast.success("Download realizado com sucesso!")
    } catch (error: any) {
      const errorMessage = error?.message || error?.msg
      toast.error(errorMessage ?? "Eita, deu ruim pra baixar o excel")
    } finally {
      setIsLoadingDownload(false)
    }
  }

  const { getRootProps, getInputProps, isDragAccept, isDragActive, isDragReject } = useDropzone({
    onDropAccepted,
    accept: ".xlsx, .xls, .xml, .csv" as unknown as Accept,
    multiple: false,
  })

  return (
    <Modal
      title="Criação em massa"
      open={isOpen}
      handleClose={handleCloseDialog}
      style={{ width: "40rem", borderRadius: "1rem" }}
    >
      <Box
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
        }}
      >
        <Typography style={{ marginBottom: "2rem" }}>
          Baixe o arquivo modelo abaixo e preencha-o. O Arquivo deve ser no formato .xlsx.
        </Typography>
        <BtnDownload
          onClick={handleExcelDownload}
          disabled={isLoadingDownload}
          style={{ marginBottom: "2rem" }}
        >
          {isLoadingDownload ? "Baixando..." : "Baixar Modelo"}
        </BtnDownload>
        <Typography>Em seguida, envie a planilha abaixo.</Typography>
      </Box>

      <Dropzone
        {...getRootProps({
          className: `${isDragActive ? DragActive : ""} ${isDragAccept ? DragAccept : ""} ${isDragReject ? DragReject : ""}`,
        })}
      >
        <input {...getInputProps()} disabled={isUploading === true} />
        {isUploading === false && fileName ? (
          <Typography>
            <CheckCircleOutlineIcon />
            {fileName}
          </Typography>
        ) : isDragReject ? (
          <Typography>O tipo desse arquivo não é aceito</Typography>
        ) : (
          <Typography>
            {fileName === ""
              ? "Arraste o arquivo de excel pra cá ou clica aqui pra buscar ele"
              : fileName}
          </Typography>
        )}
      </Dropzone>

      {!isUploading ? (
        <BtnUpload onClick={handleCreateBatches} disabled={isLoadingUpload || !fileName}>
          {isLoadingUpload ? "Enviando, aguarde..." : "Enviar"}
        </BtnUpload>
      ) : (
        <div>
          <LinearProgress variant="buffer" value={100} valueBuffer={100} />
        </div>
      )}
    </Modal>
  )
}
